/**
 * 8x8 pixel-art sprites, ported from the design handoff (sprites.jsx).
 *
 * Convention for pixel grid values:
 *   0 = transparent
 *   1 = light                (palette.light)
 *   2 = mid                  (palette.mid)
 *   3 = dark                 (palette.dark)
 *   4 = outline (#0a0510)
 *   5 = ink     (#f4e4c1)    eye whites / armor highlights
 *   6 = bad     (#d04848)    boss red glow eyes
 *   7 = accent  (#f0a040)    boss crown / warm metal trims
 */

export type PixelGrid = readonly (readonly number[])[];

export const SPRITE_SIZE = 8;
export const OUTLINE_COLOR = "#0a0510";

/** Faceted gem shape — top-left lit, bottom-right shadowed. */
export const GEM_SPRITE: PixelGrid = [
  [0, 0, 4, 4, 4, 4, 0, 0],
  [0, 4, 1, 1, 2, 2, 4, 0],
  [4, 1, 1, 2, 2, 2, 3, 4],
  [4, 1, 2, 2, 2, 3, 3, 4],
  [4, 2, 2, 2, 3, 3, 3, 4],
  [4, 2, 2, 3, 3, 3, 3, 4],
  [0, 4, 3, 3, 3, 3, 4, 0],
  [0, 0, 4, 4, 4, 4, 0, 0],
];

/** Basic creep shape (legacy 8x8 fallback — replaced by per-archetype sprites). */
export const CREEP_SPRITE: PixelGrid = [
  [0, 0, 4, 4, 4, 4, 0, 0],
  [0, 4, 2, 2, 2, 2, 4, 0],
  [4, 2, 1, 2, 2, 1, 2, 4],
  [4, 2, 4, 2, 2, 4, 2, 4],
  [4, 2, 2, 2, 2, 2, 2, 4],
  [4, 2, 2, 4, 4, 2, 2, 4],
  [0, 4, 2, 2, 2, 2, 4, 0],
  [0, 0, 4, 0, 0, 4, 0, 0],
];

/* -------------------------------------------------------------------------
 * 12x12 archetype sprites (Variant B "Cobblestone Keep").
 * ------------------------------------------------------------------------- */

/** SHAMBLER — amorphous blob with eyes at wrong heights, pseudopod drips. */
export const SPRITE_SHAMBLER: PixelGrid = [
  [0, 0, 0, 4, 4, 4, 4, 4, 4, 0, 0, 0],
  [0, 0, 4, 1, 1, 2, 2, 2, 1, 4, 0, 0],
  [0, 4, 1, 4, 5, 2, 2, 2, 1, 1, 4, 0],
  [0, 4, 2, 2, 2, 2, 2, 2, 2, 2, 4, 0],
  [4, 1, 2, 2, 2, 2, 2, 4, 5, 2, 1, 4],
  [4, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 4],
  [4, 2, 4, 5, 2, 2, 2, 2, 2, 2, 2, 4],
  [4, 2, 2, 2, 2, 3, 3, 2, 2, 3, 2, 4],
  [0, 4, 3, 3, 3, 3, 3, 3, 3, 3, 4, 0],
  [0, 0, 4, 2, 4, 3, 3, 4, 2, 4, 0, 0],
  [0, 0, 4, 0, 0, 4, 4, 0, 0, 4, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
];

/** SKITTER — low-slung spider body, oversized eye, legs sprawling outward. */
export const SPRITE_SKITTER: PixelGrid = [
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
  [0, 0, 0, 4, 1, 1, 1, 1, 4, 0, 0, 0],
  [0, 0, 4, 1, 4, 5, 5, 4, 1, 4, 0, 0],
  [0, 4, 2, 2, 2, 4, 4, 2, 2, 2, 4, 0],
  [4, 1, 4, 2, 2, 2, 2, 2, 2, 4, 1, 4],
  [0, 4, 2, 2, 2, 2, 2, 2, 2, 2, 4, 0],
  [4, 0, 4, 3, 2, 2, 2, 2, 3, 4, 0, 4],
  [0, 4, 0, 4, 3, 3, 3, 3, 4, 0, 4, 0],
  [4, 0, 0, 0, 4, 3, 3, 4, 0, 0, 0, 4],
  [0, 0, 0, 0, 0, 4, 4, 0, 0, 0, 0, 0],
  [0, 0, 3, 0, 0, 0, 0, 0, 0, 3, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
];

/** CARAPACE — solid chitin dome, eyes barely visible through a slit. */
export const SPRITE_CARAPACE: PixelGrid = [
  [0, 0, 4, 4, 4, 4, 4, 4, 4, 4, 0, 0],
  [0, 4, 3, 3, 1, 1, 1, 1, 3, 3, 4, 0],
  [4, 3, 1, 1, 2, 2, 2, 2, 1, 1, 3, 4],
  [4, 3, 4, 4, 4, 4, 4, 4, 4, 4, 3, 4],
  [4, 3, 3, 4, 5, 3, 3, 5, 4, 3, 3, 4],
  [4, 3, 4, 4, 4, 4, 4, 4, 4, 4, 3, 4],
  [4, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 4],
  [4, 3, 2, 3, 4, 2, 2, 4, 3, 2, 3, 4],
  [4, 3, 3, 2, 2, 2, 2, 2, 2, 3, 3, 4],
  [0, 4, 3, 3, 3, 4, 4, 3, 3, 3, 4, 0],
  [0, 0, 4, 4, 3, 3, 3, 3, 4, 4, 0, 0],
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
];

/** AIR — bright bat/wisp with detached drop-shadow underneath. */
/** SHRIKE — flayed wing horror, tattered membrane wings with eye-spots, bone struts. */
export const SPRITE_SHRIKE: PixelGrid = [
  [4, 4, 0, 0, 0, 0, 0, 0, 0, 0, 4, 4],
  [4, 1, 4, 0, 0, 0, 0, 0, 0, 4, 1, 4],
  [4, 2, 7, 4, 0, 4, 4, 0, 4, 7, 2, 4],
  [4, 5, 2, 7, 4, 1, 1, 4, 7, 2, 5, 4],
  [4, 2, 2, 1, 4, 5, 5, 4, 1, 2, 2, 4],
  [0, 4, 5, 2, 1, 4, 4, 1, 2, 5, 4, 0],
  [0, 4, 2, 2, 1, 1, 1, 1, 2, 2, 4, 0],
  [0, 0, 4, 2, 2, 6, 6, 2, 2, 4, 0, 0],
  [0, 0, 0, 4, 4, 2, 2, 4, 4, 0, 0, 0],
  [0, 0, 0, 0, 0, 4, 4, 0, 0, 0, 0, 0],
  [0, 0, 0, 4, 0, 0, 0, 0, 4, 0, 0, 0],
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
];

/** AMALGAM — fused mass with bone spikes, stacked eye-pairs of red and white. */
export const SPRITE_AMALGAM: PixelGrid = [
  [0, 0, 5, 0, 5, 4, 4, 5, 0, 5, 0, 0],
  [0, 0, 4, 5, 4, 5, 5, 4, 5, 4, 0, 0],
  [0, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 0],
  [4, 3, 1, 1, 2, 2, 2, 2, 1, 1, 3, 4],
  [4, 1, 4, 6, 2, 2, 2, 2, 6, 4, 1, 4],
  [4, 2, 2, 4, 5, 2, 2, 5, 4, 2, 2, 4],
  [4, 2, 2, 2, 2, 4, 4, 2, 2, 2, 2, 4],
  [4, 2, 2, 4, 5, 4, 4, 5, 4, 2, 2, 4],
  [4, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 4],
  [4, 3, 3, 2, 2, 2, 2, 2, 2, 3, 3, 4],
  [0, 4, 3, 3, 3, 3, 3, 3, 3, 3, 4, 0],
  [0, 0, 4, 4, 4, 4, 4, 4, 4, 4, 0, 0],
];

/** MENDER — jellyfish bell with glowing amber organ, dangling tendrils. */
export const SPRITE_MENDER: PixelGrid = [
  [0, 0, 0, 4, 4, 4, 4, 4, 4, 0, 0, 0],
  [0, 0, 4, 1, 1, 1, 1, 1, 1, 4, 0, 0],
  [0, 4, 1, 1, 2, 2, 2, 2, 1, 1, 4, 0],
  [0, 4, 2, 4, 5, 2, 2, 5, 4, 2, 4, 0],
  [0, 4, 2, 2, 2, 7, 7, 2, 2, 2, 4, 0],
  [0, 0, 4, 2, 2, 7, 7, 2, 2, 4, 0, 0],
  [0, 4, 0, 4, 3, 2, 2, 3, 4, 0, 4, 0],
  [4, 0, 0, 4, 0, 4, 4, 0, 4, 0, 0, 4],
  [0, 4, 0, 0, 4, 0, 0, 4, 0, 0, 4, 0],
  [0, 0, 4, 0, 0, 4, 4, 0, 0, 4, 0, 0],
  [0, 0, 0, 4, 0, 0, 0, 0, 4, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
];

/** WIZARD — arcane sage with sigil hat and flowing robes. */
export const SPRITE_WIZARD: PixelGrid = [
  [0, 0, 0, 0, 0, 4, 4, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 4, 1, 1, 4, 0, 0, 0, 0],
  [0, 0, 0, 4, 1, 1, 7, 2, 4, 0, 0, 0],
  [0, 0, 4, 4, 4, 4, 4, 4, 4, 4, 0, 0],
  [0, 0, 0, 4, 1, 2, 2, 1, 4, 0, 0, 0],
  [0, 0, 4, 2, 4, 5, 5, 4, 2, 4, 0, 0],
  [0, 0, 4, 2, 2, 2, 2, 2, 2, 4, 0, 0],
  [0, 4, 1, 4, 2, 7, 7, 2, 4, 1, 4, 0],
  [7, 0, 0, 4, 3, 2, 2, 3, 4, 0, 0, 7],
  [0, 0, 0, 4, 3, 3, 3, 3, 4, 0, 0, 0],
  [0, 0, 0, 0, 4, 3, 3, 4, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 4, 4, 0, 0, 0, 0, 0],
];

/** BURROWER — segmented worm with vestigial eyes along segments. */
export const SPRITE_BURROWER: PixelGrid = [
  [0, 0, 0, 4, 4, 4, 4, 4, 4, 0, 0, 0],
  [0, 0, 4, 1, 1, 2, 2, 1, 1, 4, 0, 0],
  [0, 4, 1, 4, 5, 2, 2, 5, 4, 1, 4, 0],
  [0, 4, 2, 2, 2, 4, 4, 2, 2, 2, 4, 0],
  [0, 4, 4, 2, 4, 5, 5, 4, 2, 4, 4, 0],
  [0, 4, 2, 2, 2, 2, 2, 2, 2, 2, 4, 0],
  [0, 4, 4, 2, 2, 2, 2, 2, 2, 4, 4, 0],
  [0, 4, 2, 2, 4, 5, 2, 2, 2, 2, 4, 0],
  [0, 4, 2, 3, 3, 3, 3, 3, 3, 2, 4, 0],
  [0, 0, 4, 3, 3, 3, 3, 3, 3, 4, 0, 0],
  [3, 0, 0, 4, 4, 3, 3, 4, 4, 0, 0, 3],
  [0, 3, 0, 0, 0, 4, 4, 0, 0, 0, 3, 0],
];

/** VESSEL — cracked clay jar with eye-holes. Shatters on death. */
export const SPRITE_VESSEL: PixelGrid = [
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
  [0, 0, 0, 4, 1, 1, 1, 1, 4, 0, 0, 0],
  [0, 0, 4, 1, 1, 2, 2, 1, 1, 4, 0, 0],
  [0, 4, 1, 2, 2, 2, 2, 2, 2, 1, 4, 0],
  [4, 1, 2, 4, 5, 2, 2, 4, 5, 2, 1, 4],
  [4, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 4],
  [4, 2, 2, 4, 2, 2, 2, 2, 4, 2, 2, 4],
  [4, 2, 4, 2, 2, 2, 2, 2, 2, 4, 2, 4],
  [4, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 4],
  [0, 4, 3, 3, 2, 2, 2, 2, 3, 3, 4, 0],
  [0, 0, 4, 3, 3, 4, 4, 3, 3, 4, 0, 0],
  [0, 0, 0, 4, 4, 0, 0, 4, 4, 0, 0, 0],
];

/** GAZER — amorphous mass of fused eyeballs. */
export const SPRITE_GAZER: PixelGrid = [
  [0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0, 0],
  [0, 0, 4, 1, 1, 2, 2, 4, 4, 0, 0, 0],
  [0, 4, 1, 4, 5, 2, 4, 5, 1, 4, 0, 0],
  [4, 1, 2, 2, 2, 2, 1, 2, 2, 2, 4, 0],
  [4, 2, 4, 5, 2, 2, 2, 2, 4, 5, 1, 4],
  [4, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 4],
  [4, 2, 2, 2, 4, 5, 5, 4, 2, 2, 2, 4],
  [4, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 4],
  [0, 4, 3, 2, 4, 5, 2, 2, 2, 3, 4, 0],
  [0, 0, 4, 3, 2, 2, 3, 3, 3, 4, 0, 0],
  [0, 0, 0, 4, 3, 3, 3, 3, 4, 0, 0, 0],
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
];

/** CORAL — branching crystalline growth, brain coral meets frost. */
export const SPRITE_CORAL: PixelGrid = [
  [0, 4, 0, 0, 0, 0, 0, 0, 0, 0, 4, 0],
  [4, 1, 4, 0, 0, 4, 4, 0, 0, 4, 1, 4],
  [0, 4, 1, 4, 4, 1, 1, 4, 4, 1, 4, 0],
  [0, 0, 4, 1, 1, 1, 1, 1, 1, 4, 0, 0],
  [0, 0, 4, 2, 4, 5, 5, 4, 2, 4, 0, 0],
  [0, 4, 2, 2, 2, 2, 2, 2, 2, 2, 4, 0],
  [4, 1, 4, 2, 2, 2, 2, 2, 2, 4, 1, 4],
  [0, 4, 2, 2, 3, 2, 2, 3, 2, 2, 4, 0],
  [0, 0, 4, 3, 3, 3, 3, 3, 3, 4, 0, 0],
  [0, 4, 3, 4, 0, 4, 4, 0, 4, 3, 4, 0],
  [4, 3, 4, 0, 0, 0, 0, 0, 0, 4, 3, 4],
  [0, 4, 0, 0, 0, 0, 0, 0, 0, 0, 4, 0],
];

/** ANEMONE — tube creature with waving tendrils. */
export const SPRITE_ANEMONE: PixelGrid = [
  [4, 0, 4, 0, 4, 0, 0, 4, 0, 4, 0, 4],
  [0, 4, 0, 4, 0, 4, 4, 0, 4, 0, 4, 0],
  [0, 0, 4, 1, 4, 1, 1, 4, 1, 4, 0, 0],
  [0, 0, 4, 1, 1, 1, 1, 1, 1, 4, 0, 0],
  [0, 0, 4, 1, 4, 5, 5, 4, 1, 4, 0, 0],
  [0, 0, 4, 2, 2, 2, 2, 2, 2, 4, 0, 0],
  [0, 4, 2, 2, 2, 2, 2, 2, 2, 2, 4, 0],
  [0, 4, 2, 2, 3, 2, 2, 3, 2, 2, 4, 0],
  [0, 4, 2, 3, 3, 3, 3, 3, 3, 2, 4, 0],
  [0, 0, 4, 3, 3, 3, 3, 3, 3, 4, 0, 0],
  [0, 0, 0, 4, 3, 3, 3, 3, 4, 0, 0, 0],
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
];

/** CHRYSALID (shell) — smooth oval cocoon with horizontal seam lines. */
export const SPRITE_CHRYSALID: PixelGrid = [
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
  [0, 0, 0, 4, 3, 3, 3, 3, 4, 0, 0, 0],
  [0, 0, 4, 3, 2, 2, 2, 2, 3, 4, 0, 0],
  [0, 4, 3, 2, 2, 4, 4, 2, 2, 3, 4, 0],
  [0, 4, 3, 2, 4, 5, 5, 4, 2, 3, 4, 0],
  [0, 4, 3, 2, 2, 4, 4, 2, 2, 3, 4, 0],
  [0, 4, 3, 2, 2, 2, 2, 2, 2, 3, 4, 0],
  [0, 4, 3, 3, 4, 2, 2, 4, 3, 3, 4, 0],
  [0, 0, 4, 3, 3, 2, 2, 3, 3, 4, 0, 0],
  [0, 0, 4, 3, 3, 3, 3, 3, 3, 4, 0, 0],
  [0, 0, 0, 4, 3, 3, 3, 3, 4, 0, 0, 0],
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
];

/** CHRYSALID (awakened) — Ancient One: eldritch bloom with spreading tendrils. */
export const SPRITE_CHRYSALID_AWAKE: PixelGrid = [
  [3, 0, 0, 0, 5, 4, 4, 5, 0, 0, 0, 3],
  [0, 0, 0, 4, 1, 1, 1, 1, 4, 0, 0, 0],
  [0, 0, 4, 1, 1, 4, 4, 1, 1, 4, 0, 0],
  [0, 4, 1, 2, 4, 7, 7, 4, 2, 1, 4, 0],
  [4, 1, 2, 2, 2, 4, 4, 2, 2, 2, 1, 4],
  [4, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 4],
  [4, 1, 3, 2, 5, 2, 2, 5, 2, 3, 1, 4],
  [0, 4, 3, 3, 2, 2, 2, 2, 3, 3, 4, 0],
  [4, 0, 4, 3, 4, 3, 3, 4, 3, 4, 0, 4],
  [0, 4, 0, 4, 0, 4, 4, 0, 4, 0, 4, 0],
  [4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 4],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
];

/** MYCOID — upright toadstool with rising spore particles. */
export const SPRITE_MYCOID: PixelGrid = [
  [0, 0, 0, 2, 0, 0, 0, 0, 2, 0, 0, 0],
  [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0],
  [0, 0, 4, 4, 1, 1, 1, 1, 4, 4, 0, 0],
  [0, 4, 1, 1, 1, 2, 2, 1, 1, 1, 4, 0],
  [4, 1, 1, 2, 4, 5, 5, 4, 2, 1, 1, 4],
  [4, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 4],
  [0, 4, 4, 2, 2, 2, 2, 2, 2, 4, 4, 0],
  [0, 0, 0, 4, 2, 2, 2, 2, 4, 0, 0, 0],
  [0, 0, 0, 4, 3, 2, 2, 3, 4, 0, 0, 0],
  [0, 0, 0, 4, 3, 3, 3, 3, 4, 0, 0, 0],
  [0, 0, 4, 3, 3, 3, 3, 3, 3, 4, 0, 0],
  [0, 0, 0, 4, 4, 4, 4, 4, 4, 0, 0, 0],
];

/** GESTATION (calm) — The Congregation: fused bodies pressing outward. 27x27 at 2px/cell = 54x54. */
export const SPRITE_GESTATION: PixelGrid = [
  [
    0, 0, 0, 0, 0, 0, 4, 4, 0, 0, 0, 0, 0, 4, 4, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 0, 4, 1, 7, 4, 0, 0, 0, 4, 1, 1, 7, 4, 0, 0, 0, 4, 4, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 4, 1, 1, 1, 4, 0, 0, 4, 1, 6, 6, 1, 4, 0, 0, 4, 1, 7, 4, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 4, 1, 2, 2, 1, 4, 0, 4, 1, 4, 5, 5, 4, 1, 4, 0, 4, 1, 1, 1, 4, 0,
    0, 0,
  ],
  [
    0, 0, 4, 7, 1, 4, 4, 2, 1, 4, 1, 2, 2, 3, 3, 2, 2, 1, 4, 1, 2, 6, 2, 1, 4,
    0, 0,
  ],
  [
    0, 4, 1, 1, 2, 5, 5, 2, 2, 1, 2, 2, 3, 3, 3, 3, 2, 2, 1, 2, 4, 5, 4, 2, 4,
    0, 0,
  ],
  [
    0, 4, 7, 2, 2, 4, 4, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 2, 2, 1,
    4, 0,
  ],
  [
    4, 1, 2, 6, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 2, 6, 2, 2, 1, 7,
    4, 0,
  ],
  [
    4, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 6, 2, 1,
    4, 0,
  ],
  [
    4, 6, 2, 2, 3, 3, 3, 3, 3, 5, 5, 3, 3, 3, 5, 5, 3, 3, 3, 3, 3, 2, 2, 2, 4,
    0, 0,
  ],
  [
    4, 2, 2, 3, 3, 3, 3, 5, 5, 5, 5, 5, 3, 5, 5, 5, 5, 3, 3, 3, 3, 3, 2, 4, 0,
    0, 0,
  ],
  [
    4, 7, 2, 3, 3, 3, 3, 3, 5, 5, 3, 3, 3, 3, 5, 5, 3, 3, 3, 3, 3, 3, 4, 0, 0,
    0, 0,
  ],
  [
    4, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 4, 4, 3, 3, 3, 3, 3, 3, 3, 3, 4, 1, 4, 0,
    0, 0,
  ],
  [
    4, 6, 2, 2, 3, 3, 3, 3, 3, 4, 4, 3, 3, 4, 4, 3, 3, 3, 3, 3, 4, 1, 2, 1, 4,
    0, 0,
  ],
  [
    0, 4, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 4, 1, 2, 6, 2, 4,
    0, 0,
  ],
  [
    0, 4, 7, 2, 2, 3, 3, 3, 3, 5, 3, 3, 3, 3, 5, 3, 3, 3, 4, 1, 2, 2, 2, 4, 0,
    0, 0,
  ],
  [
    0, 0, 4, 2, 2, 3, 3, 5, 5, 5, 5, 3, 3, 5, 5, 5, 5, 4, 1, 2, 2, 4, 4, 0, 0,
    0, 0,
  ],
  [
    0, 0, 4, 6, 2, 2, 3, 3, 5, 5, 3, 3, 3, 3, 5, 5, 2, 2, 6, 2, 4, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 4, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 4, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 4, 1, 2, 6, 2, 2, 3, 3, 3, 3, 3, 3, 2, 2, 6, 2, 4, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 4, 1, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3, 2, 2, 2, 2, 4, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 4, 7, 1, 2, 6, 2, 2, 2, 2, 4, 4, 2, 2, 2, 6, 4, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 4, 4, 2, 2, 2, 4, 4, 4, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 4, 4, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
];

/** GESTATION (enraged, <50% HP) — eyes blazing, arms breaking out, more exposed flesh. */
export const SPRITE_GESTATION_ENRAGED: PixelGrid = [
  [
    0, 0, 0, 0, 0, 4, 4, 4, 0, 0, 0, 0, 0, 4, 4, 4, 0, 0, 0, 0, 4, 4, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 4, 7, 1, 7, 4, 0, 0, 0, 4, 1, 7, 1, 4, 0, 0, 4, 7, 1, 4, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 4, 1, 1, 6, 1, 1, 4, 0, 4, 1, 6, 6, 6, 1, 4, 4, 1, 1, 6, 1, 4, 0,
    0, 0,
  ],
  [
    0, 0, 4, 7, 1, 5, 5, 5, 1, 4, 4, 1, 5, 5, 5, 5, 5, 1, 4, 1, 5, 5, 5, 1, 4,
    0, 0,
  ],
  [
    0, 4, 1, 1, 2, 5, 5, 5, 2, 1, 2, 2, 5, 3, 3, 5, 2, 2, 1, 2, 5, 5, 5, 2, 1,
    4, 0,
  ],
  [
    4, 7, 1, 2, 2, 4, 4, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 2, 2, 2, 4, 4, 2, 2, 1,
    7, 4,
  ],
  [
    4, 1, 2, 6, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 6, 2, 2, 2,
    1, 4,
  ],
  [
    4, 2, 2, 2, 6, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 6, 2, 2, 6, 2,
    2, 4,
  ],
  [
    4, 6, 2, 3, 3, 3, 3, 3, 3, 5, 5, 5, 3, 3, 5, 5, 5, 3, 3, 3, 3, 3, 2, 2, 6,
    4, 0,
  ],
  [
    4, 2, 3, 3, 3, 3, 3, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 3, 3, 3, 3, 3, 2, 4,
    0, 0,
  ],
  [
    4, 7, 2, 3, 3, 3, 5, 5, 5, 5, 5, 5, 3, 5, 5, 5, 5, 5, 5, 3, 3, 3, 3, 4, 0,
    0, 0,
  ],
  [
    4, 2, 2, 3, 3, 3, 3, 5, 5, 5, 5, 3, 3, 3, 5, 5, 5, 5, 3, 3, 3, 3, 4, 6, 4,
    0, 0,
  ],
  [
    4, 6, 2, 2, 3, 3, 3, 3, 5, 3, 3, 4, 4, 3, 3, 5, 3, 3, 3, 3, 3, 4, 1, 2, 1,
    4, 0,
  ],
  [
    4, 2, 2, 3, 3, 3, 3, 3, 4, 4, 4, 3, 3, 4, 4, 4, 3, 3, 3, 3, 4, 1, 2, 6, 2,
    4, 0,
  ],
  [
    0, 4, 2, 2, 3, 3, 3, 3, 3, 5, 3, 3, 3, 3, 5, 3, 3, 3, 3, 4, 1, 2, 2, 2, 4,
    0, 0,
  ],
  [
    0, 4, 6, 2, 3, 3, 3, 5, 5, 5, 5, 5, 3, 5, 5, 5, 5, 5, 3, 4, 2, 6, 2, 4, 0,
    0, 0,
  ],
  [
    0, 4, 7, 2, 2, 3, 3, 5, 5, 5, 5, 3, 3, 3, 5, 5, 5, 5, 4, 2, 2, 2, 4, 0, 0,
    0, 0,
  ],
  [
    0, 0, 4, 2, 2, 3, 3, 3, 5, 5, 3, 3, 3, 3, 3, 5, 5, 2, 2, 6, 2, 4, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 4, 6, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 4, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 4, 7, 2, 6, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 6, 2, 4, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    4, 1, 2, 2, 2, 6, 2, 2, 3, 3, 3, 3, 3, 3, 2, 2, 6, 2, 4, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    4, 7, 1, 2, 6, 2, 2, 2, 2, 4, 4, 4, 4, 2, 2, 2, 2, 4, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 4, 4, 2, 2, 2, 4, 4, 4, 0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 4, 4, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
  [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0,
  ],
];

import type { CreepKind } from "../data/creeps";

export const SPRITE_BY_KIND: Record<CreepKind, PixelGrid> = {
  shambler: SPRITE_SHAMBLER,
  skitter: SPRITE_SKITTER,
  carapace: SPRITE_CARAPACE,
  shrike: SPRITE_SHRIKE,
  amalgam: SPRITE_AMALGAM,
  mender: SPRITE_MENDER,
  wizard: SPRITE_WIZARD,
  burrower: SPRITE_BURROWER,
  vessel: SPRITE_VESSEL,
  gazer: SPRITE_GAZER,
  coral: SPRITE_CORAL,
  anemone: SPRITE_ANEMONE,
  chrysalid: SPRITE_CHRYSALID,
  mycoid: SPRITE_MYCOID,
  gestation: SPRITE_GESTATION,
};

/** Heart for the lives chip. */
export const HEART_SPRITE: PixelGrid = [
  [0, 4, 4, 0, 0, 4, 4, 0],
  [4, 2, 1, 4, 4, 2, 1, 4],
  [4, 2, 2, 2, 2, 2, 1, 4],
  [4, 2, 2, 2, 2, 2, 2, 4],
  [0, 4, 2, 2, 2, 2, 4, 0],
  [0, 0, 4, 2, 2, 4, 0, 0],
  [0, 0, 0, 4, 4, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0],
];

/** Coin for the gold chip. */
export const COIN_SPRITE: PixelGrid = [
  [0, 0, 4, 4, 4, 4, 0, 0],
  [0, 4, 1, 2, 2, 2, 4, 0],
  [4, 1, 2, 4, 4, 2, 2, 4],
  [4, 2, 2, 4, 2, 2, 2, 4],
  [4, 2, 2, 4, 2, 2, 2, 4],
  [4, 2, 2, 4, 4, 2, 2, 4],
  [0, 4, 2, 2, 2, 2, 4, 0],
  [0, 0, 4, 4, 4, 4, 0, 0],
];

/** Rock — blocks pathing. */
export const ROCK_SPRITE: PixelGrid = [
  [0, 0, 4, 4, 4, 4, 0, 0],
  [0, 4, 1, 2, 2, 2, 3, 4],
  [4, 1, 2, 2, 2, 3, 3, 4],
  [4, 2, 2, 4, 2, 2, 3, 4],
  [4, 2, 4, 2, 2, 2, 3, 4],
  [4, 2, 2, 2, 4, 2, 3, 4],
  [0, 4, 3, 3, 3, 3, 4, 0],
  [0, 0, 4, 4, 4, 4, 0, 0],
];

export const ROCK_PALETTE = {
  light: "#a09080",
  mid: "#7a6a5a",
  dark: "#4a3a2a",
};

/** Stone tower base, drawn as a rectangle separately (not a sprite grid). */
export const TOWER_BASE = {
  fill: 0x7a6a5a,
  highlight: 0x9a8a7a,
  shadow: 0x4a3a2a,
  outline: 0x2a1a10,
};
